'use client'

import Image from 'next/image'
import { useLocale, useTranslations } from 'next-intl'
import { useLowStock } from '@/components/menu/LowStockContext'
import { LowStockBadge } from '@/components/menu/LowStockBadge'
import { motion } from 'framer-motion'
import { menuPhotoUrl } from '@/lib/utils/imageTransform'
import { focalPosition } from '@/lib/menu/photoFocal'
import { effectiveItemCardPrice, type PricingConfig } from '@/lib/utils/pricing'
import { localizedItem, type ItemTranslations } from '@/lib/menu/localizeItem'
import { REASON_KEY } from '@/lib/suggestions/reasonKeys'
import type { SuggestionReason } from '@/lib/suggestions/types'
import type { MicrositeItem } from './RestaurantMicrosite'
import { useScrollLock } from './SheetPortal'

/** Una sugerencia del motor ya resuelta contra el menú del microsite. */
export type UpsellSuggestion = { item: MicrositeItem; reason: SuggestionReason; score: number }

type Props = {
  /** Nombre canónico + traducciones: la hoja muestra el del idioma del cliente. */
  addedItem: { name: string; price: number; translations?: ItemTranslations | null }
  /** null = el dueño apagó las sugerencias de la tienda: solo confirmación y CTAs. */
  suggestions: UpsellSuggestion[] | null
  /** true mientras la acción del motor responde: esqueleto en vez de "no hay más". */
  loading: boolean
  cartTotal: number
  cartCount: number
  accentColor: string
  pricingCfg: PricingConfig
  onAddSuggestion: (s: UpsellSuggestion, index: number) => void
  onContinueShopping: () => void
  onViewCart: () => void
}

const SKELETON_CARDS = 3

export function UpsellSheet({
  addedItem,
  suggestions,
  loading,
  cartTotal,
  accentColor,
  pricingCfg,
  onAddSuggestion,
  onContinueShopping,
  onViewCart,
}: Props) {
  const t = useTranslations('public.microsite.upsell')
  // Copy del cliente, compartido con el kiosko (lib/suggestions/reasonKeys.ts):
  // el microsite recibe el bundle completo, no hace falta una copia propia.
  const tReason = useTranslations('kiosk.checkout')
  const locale = useLocale() === 'en' ? 'en' : 'es'
  const addedName = localizedItem(addedItem, locale).name
  const lowStock = useLowStock()
  useScrollLock()
  const enabled = suggestions !== null
  // Hoja alta solo si hay algo que mostrar (esqueleto o tarjetas); sin resultados queda compacta.
  const tall = enabled && (loading || suggestions.length > 0)
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/50 flex items-end justify-center"
      onClick={onContinueShopping}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        // text-gray-900: mismo reset que OrderSheet — hoja blanca dentro del
        // wrapper del tema (color: var(--ms-text)); sin él, cualquier texto sin
        // clase de color explícita hereda el marfil del tema dark y se pierde.
        className="relative w-full max-w-2xl bg-white text-gray-900 rounded-t-3xl flex flex-col"
        style={{ height: tall ? '65dvh' : 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div className="pt-3 pb-2 flex justify-center">
          <div className="w-12 h-1.5 rounded-full bg-gray-300" />
        </div>

        {/* Header */}
        <div className="px-5 pt-2 pb-3">
          <p className="text-xl font-black text-gray-900">{t('added')}</p>
          <p className="text-sm text-gray-500 mt-0.5 truncate">{addedName}</p>
          {enabled && <p className="text-base font-bold text-gray-900 mt-3">{t('anythingElse')}</p>}
        </div>

        {/* Suggestions horizontal scroll */}
        {suggestions !== null && (
          <div className="flex-1 min-h-0 overflow-y-hidden">
            {loading ? (
              <div className="overflow-x-hidden px-5 pb-4" aria-busy="true" aria-live="polite">
                <p className="sr-only">{t('loading')}</p>
                <div className="flex gap-3">
                  {Array.from({ length: SKELETON_CARDS }).map((_, i) => (
                    <div key={i} className="w-36 flex-shrink-0 rounded-2xl overflow-hidden border border-gray-100 animate-pulse">
                      <div className="aspect-square bg-gray-100" />
                      <div className="p-2.5 space-y-2">
                        <div className="h-3 bg-gray-100 rounded" />
                        <div className="h-3 w-2/3 bg-gray-100 rounded" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : suggestions.length === 0 ? (
              <div className="px-5 py-6 text-sm text-gray-500">{t('noMore')}</div>
            ) : (
              <div className="overflow-x-auto snap-x scroll-smooth px-5 pb-4">
                <div className="flex gap-3">
                  {suggestions.map((s, index) => {
                    const cardPrice = effectiveItemCardPrice(s.item.price, pricingCfg)
                    // Solo display: al carrito va la sugerencia entera (nombre canónico).
                    const name = localizedItem(s.item, locale).name
                    return (
                      <div
                        key={s.item.id}
                        className="snap-start w-36 flex-shrink-0 bg-white shadow-sm rounded-2xl overflow-hidden border border-gray-100"
                      >
                        <div className="relative aspect-square bg-gray-100">
                          {s.item.image_url ? (
                            <Image
                              src={menuPhotoUrl(s.item.image_url, 400)}
                              alt={name}
                              fill
                              sizes="144px"
                              className="object-cover"
                              style={{ objectPosition: focalPosition(s.item.image_focal) }}
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-100" />
                          )}
                          {lowStock.item(s.item.id) && (
                            <span className="absolute bottom-1.5 left-1.5">
                              <LowStockBadge size="sm" />
                            </span>
                          )}
                        </div>
                        <div className="p-2.5">
                          <p className="text-[11px] text-gray-500 leading-tight truncate">{tReason(REASON_KEY[s.reason])}</p>
                          <p className="text-xs font-bold text-gray-900 line-clamp-2 leading-tight min-h-[2rem] mt-0.5">
                            {name}
                          </p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs font-bold" style={{ color: accentColor }}>
                              ${cardPrice.toFixed(2)}
                            </span>
                            <button
                              type="button"
                              onClick={() => onAddSuggestion(s, index)}
                              className="w-7 h-7 rounded-full text-white font-bold flex items-center justify-center transition active:scale-95"
                              style={{ backgroundColor: accentColor }}
                              aria-label={t('addAria', { name })}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* CTAs */}
        <div
          className="flex-shrink-0 px-4 pt-4 border-t border-gray-100 bg-white flex gap-3"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          <button
            type="button"
            onClick={onContinueShopping}
            className="flex-1 rounded-full py-3.5 text-sm font-bold border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
          >
            {t('keepBrowsing')}
          </button>
          <button
            type="button"
            onClick={onViewCart}
            className="flex-1 rounded-full py-3.5 text-sm font-bold text-white transition hover:opacity-95 active:scale-[0.99]"
            style={{ backgroundColor: accentColor }}
          >
            {t('viewCart', { total: `$${cartTotal.toFixed(2)}` })}
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}
